// Helpers for assembling the per-outing data that updateOutingsList fetches.
//
// For each outing the app fires four requests in parallel (the outing, its
// tires, its attributes, its laps) and collects them with Promise.allSettled,
// one result array per request type. Those four arrays are later indexed by
// position, so they MUST stay aligned with each other. Filtering each array
// independently (dropping only the failed entries) shifts indices and mismatches
// tires/laps/attributes across outings -- or throws when the lengths diverge.

const settledValue = (result, fallback) =>
    result && result.status === "fulfilled" ? result.value : fallback;

/**
 * Combine the four Promise.allSettled result arrays into mutually-aligned
 * arrays. An outing is kept only if its core data resolved -- the outing object
 * itself and a non-empty attributes list (both are indexed unconditionally
 * downstream and when rendering). Tires/laps fall back to [] so a single failed
 * request degrades that outing's detail instead of corrupting alignment.
 *
 * Returns { fullOutings, outingTires, outingAttributes, outingLapLists, droppedCount }.
 */
export function combineOutingResults(fullResults, tireResults, attributeResults, lapResults) {
    const fullOutings = [];
    const outingTires = [];
    const outingAttributes = [];
    const outingLapLists = [];

    const length = fullResults ? fullResults.length : 0;

    for (let i = 0; i < length; i++) {
        const outing = settledValue(fullResults[i], null);
        const attributes = settledValue(attributeResults[i], null);

        // Without the outing and its attributes we can't render or enrich it
        // safely; skip it entirely so the remaining arrays stay aligned.
        if (!outing || !Array.isArray(attributes) || attributes.length === 0) {
            continue;
        }

        fullOutings.push(outing);
        outingAttributes.push(attributes);
        outingTires.push(settledValue(tireResults[i], []));
        outingLapLists.push(settledValue(lapResults[i], []));
    }

    return {
        fullOutings,
        outingTires,
        outingAttributes,
        outingLapLists,
        droppedCount: length - fullOutings.length,
    };
}
